import cx from 'classnames';
import React from 'react';

import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '16px 32px 16px 16px',
    backgroundColor: theme.palette.primary.light,
    border: `1px solid ${theme.palette.grayScale10.main}`,
    boxShadow: '0px 2px 8px rgba(58, 57, 76, 0.08), 0px 2px 4px rgba(58, 57, 76, 0.03)',
    borderRadius: '8px',

    width: 'fit-content',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',

    '& > svg': {
      width: '20px',
      height: '20px',
      marginRight: '16px',
    },
    '& > p': {
      fontWeight: 'normal',
      fontSize: '14px',
      lineHeight: '20px',
      color: theme.palette.grayScale9.main,

      '& > button': {
        font: 'inherit',
        verticalAlign: 'top', // Line up with text
        textDecoration: 'underline',
        color: theme.palette.orange.main,

        '&:hover': {
          color: theme.palette.primary.main,
        },
      },
    },
  },
  statusRoot: {
    boxSizing: 'border-box',
    width: '100%',
    minHeight: '72px',
    padding: '14px 18px',
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    border: '1px solid',
    borderLeftWidth: '4px',
    borderRadius: '10px',

    [theme.breakpoints.down('xs')]: {
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      padding: '14px',
    },
  },
  statusInfo: {
    backgroundColor: theme.palette.grayScale8.main,
    borderColor: theme.palette.primary.light,
    borderLeftColor: theme.palette.primary.main,

    '& $statusIcon': {
      color: theme.palette.primary.dark,
    },
  },
  statusWarning: {
    backgroundColor: '#FFF8E7',
    borderColor: theme.palette.warning.main,
    borderLeftColor: theme.palette.warning.main,

    '& $statusIcon': {
      color: theme.palette.darkGray.main,
    },
  },
  statusNeutral: {
    backgroundColor: theme.palette.grayScale7.main,
    borderColor: theme.palette.grayScale10.main,
    borderLeftColor: theme.palette.grayScale4.main,

    '& $statusIcon': {
      color: theme.palette.gray.main,
    },
  },
  statusIcon: {
    flex: '0 0 auto',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '& > svg': {
      width: '24px',
      height: '24px',
    },
  },
  statusBody: {
    minWidth: 0,
    flex: '1 1 auto',
  },
  statusTitle: {
    margin: 0,
    color: theme.palette.darkGray.main,
    fontSize: '15px',
    lineHeight: '21px',
    fontWeight: 700,
  },
  statusContent: {
    margin: '2px 0 0',
    color: theme.palette.gray.main,
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: 400,
  },
  statusAction: {
    flex: '0 0 auto',
    marginLeft: 'auto',

    [theme.breakpoints.down('xs')]: {
      boxSizing: 'border-box',
      width: '100%',
      marginLeft: 0,
      paddingLeft: '38px',
    },

    '& > button, & > a': {
      minHeight: '38px',
      padding: '8px 14px',
      color: theme.palette.darkGray.main,
      backgroundColor: theme.palette.white.main,
      border: `1px solid ${theme.palette.grayScale5.main}`,
      borderRadius: '8px',
      fontSize: '14px',
      lineHeight: '20px',
      fontWeight: 600,
      textDecoration: 'none',
      whiteSpace: 'nowrap',
      transition: 'border-color 180ms ease, background-color 180ms ease',

      '&:hover': {
        backgroundColor: theme.palette.grayScale8.main,
        borderColor: theme.palette.primary.main,
      },
      '&:focus-visible': {
        outline: `3px solid ${theme.palette.primary.light}`,
        outlineOffset: '2px',
      },

      [theme.breakpoints.down('xs')]: {
        width: '100%',
      },
    },
  },
}));

/**
 * @param {{
 *   rootClass?: string;
 *   backgroundColor?: string;
 *   icon?: React.ReactNode;
 *   content?: React.ReactNode;
 *   variant?: 'default' | 'status';
 *   tone?: 'info' | 'warning' | 'neutral';
 *   title?: React.ReactNode;
 *   action?: React.ReactNode;
 * }} props
 */
const DashboardBubble = ({
  rootClass = undefined,
  backgroundColor = undefined,
  icon = null,
  content = null,
  variant = 'default',
  tone = 'info',
  title = undefined,
  action = undefined,
}) => {
  const classes = useStyles();

  if (variant === 'status') {
    const toneClass = {
      info: classes.statusInfo,
      warning: classes.statusWarning,
      neutral: classes.statusNeutral,
    }[tone] ?? classes.statusInfo;

    return (
      <section
        className={cx(classes.statusRoot, toneClass, rootClass)}
        style={{ backgroundColor }}
        role={tone === 'warning' ? 'alert' : 'status'}
        aria-live="polite"
      >
        <span className={classes.statusIcon} aria-hidden>
          {icon}
        </span>
        <div className={classes.statusBody}>
          {title && <h2 className={classes.statusTitle}>{title}</h2>}
          <p className={classes.statusContent}>{content}</p>
        </div>
        {action && <div className={classes.statusAction}>{action}</div>}
      </section>
    );
  }

  return (
    <span className={cx(classes.root, rootClass)} style={{ backgroundColor }}>
      {icon}
      <p>
        {content}
      </p>
    </span>
  );
};

export default DashboardBubble;
